import { ChangeDetectorRef, Component, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, Location as AngularLocation } from '@angular/common';
import { ItemSelectorComponent } from 'src/app/shared/components/item-selector/item-selector.component';
import { Item, ShopCategory } from 'src/app/shared/models/items.model';
import { Observable, Subscription } from 'rxjs';
import { MarketResponse } from 'src/app/shared/models/ao-data-interfaces';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ItemPriceCardComponent } from 'src/app/shared/components/item-price-card/item-price-card.component';
import { Quality } from 'src/app/shared/models/quality.model';
import { QUALITIES, getAllQualities } from 'src/app/shared/constants/qualities.constant';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute, Router } from '@angular/router';
import { LocationsLegendComponent } from 'src/app/shared/components/locations-legend/locations-legend.component';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BasePage } from 'src/app/shared/base-classes/base-page';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { Location } from 'src/app/shared/models/location.model';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';

class ItemWithQualityAndPower {
  item: Item;
  quality: Quality;
  basePower: number;
  qualityBonus: number;
  masteryBonus: number;
  masteryModifier: number;
  private prices: MarketResponse[] | null = null;
  selectedCities: Location[] = [];
  private subscriptions: Subscription[] = [];

  get itemPower(): number {
    return this.basePower + this.qualityBonus + this.masteryBonus + this.masteryModifier;
  }

  get citiesPrices(): MarketResponse[] | null {
    return this.prices?.filter((x) => this.selectedCities.includes(x.city)) ?? null;
  }

  get cheapestPrice(): MarketResponse | null {
    return this.citiesPrices?.reduce((minObj, currentObj) => (currentObj.sellPriceMin < minObj.sellPriceMin ? currentObj : minObj), this.citiesPrices[0]) ?? null;
  }

  constructor(
    item: Item,
    quality: Quality,
    masteryBonus: number,
    prices$: Observable<MarketResponse[] | null>,
    private userSettingsService: UserSettingsService
  ) {
    this.item = item;
    this.quality = quality;
    this.basePower = item.itemPower ?? 0;
    this.qualityBonus = quality.itemPowerBonus;
    this.masteryBonus = masteryBonus;
    this.masteryModifier = Math.round(this.masteryBonus * this.getBonus(this.item.tier ?? 0));

    this.subscriptions.push(this.userSettingsService.userSelectedCities$.subscribe((x) => (this.selectedCities = x)));

    this.subscriptions.push(
      prices$.subscribe((x) => {
        this.prices = x?.filter((x) => x.itemTypeId === this.item.uniqueName && x.qualityLevel === this.quality && x.sellPriceMin > 0) ?? null;
      })
    );
  }

  dispose(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  private getBonus(tier: number): number {
    switch (tier) {
      case 5:
        return 5 / 100;
      case 6:
        return 10 / 100;
      case 7:
        return 15 / 100;
      case 8:
        return 20 / 100;
      default:
        return 0;
    }
  }
}

@Component({
  selector: 'app-item-power',
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    ItemSelectorComponent,
    ItemPriceCardComponent,
    MatExpansionModule,
    LocationsLegendComponent,
  ],
  templateUrl: './item-power.component.html',
  styleUrls: ['./item-power.component.scss'],
})
export class ItemPowerComponent extends BasePage {
  masteryBonus: number = 200;

  groupRange: number = 50;
  groupedItems!: Record<number, ItemWithQualityAndPower[]>;
  groupedItemsArray: { key: number; value: ItemWithQualityAndPower[] }[] = [];

  constructor(
    route: ActivatedRoute,
    router: Router,
    urlLocation: AngularLocation,
    jsonReaderService: JsonReaderService,
    aoDataGetter: AoDataGetterService,
    albionImageService: AlbionImageService,
    userSettingsService: UserSettingsService,
    public loadingBarService: LoadingBarService,
    private sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) platformId: Object,
    cdr: ChangeDetectorRef
  ) {
    super(route, router, urlLocation, jsonReaderService, aoDataGetter, albionImageService, userSettingsService, cdr, platformId);
  }

  protected override filterItemsForPrices(items: Item[]): Item[] {
    if (this.items && this.selectedItem) {
      return this.items.filter((item) => item.combatSpechAchievement && item.combatSpechAchievement === this.selectedItem?.combatSpechAchievement);
    } else {
      return [];
    }
  }

  protected override filterItemsForSelect(items: Item[]): Item[] {
    return items.filter((item) => item.combatSpechAchievement);
  }

  protected override filterShopCategoriesForSelect(categories: ShopCategory[]): ShopCategory[] {
    return categories
      .filter((category) => this.categoryHasSpechItems(category))
      .map((category) => {
        category.subCategories = category.subCategories.filter((subCategory) => this.subCategoryHasSpechItems(subCategory));
        return category;
      });
  }

  protected override onItemSelectedActions(): void {
    this.groupItemsByPower();
    this.updateGroupedItemsArray();
  }

  private categoryHasSpechItems(category: ShopCategory): boolean {
    if (this.items) {
      return this.items.some((x) => x.shopCategory === category && x.combatSpechAchievement);
    }
    return false;
  }

  private subCategoryHasSpechItems(subCategory: ShopCategory): boolean {
    if (this.items) {
      return this.items.some((x) => x.shopSubCategory1 === subCategory && x.combatSpechAchievement);
    }
    return false;
  }

  groupItemsByPower(): void {
    const grouped: Record<number, ItemWithQualityAndPower[]> = {};

    const spechItemsWithQuality: ItemWithQualityAndPower[] = [];

    for (let item of this.filterItemsForPrices(this.items ?? [])) {
      if (!item.maxQualityLevel) {
        spechItemsWithQuality.push(new ItemWithQualityAndPower(item, QUALITIES.Normal, this.masteryBonus, this.prices$, this.userSettingsService));
      } else {
        for (let quality of getAllQualities()) {
          if (quality.index <= item.maxQualityLevel) {
            spechItemsWithQuality.push(new ItemWithQualityAndPower(item, quality, this.masteryBonus, this.prices$, this.userSettingsService));
          }
        }
      }
    }
    spechItemsWithQuality.forEach((item) => {
      if (item.item.itemPower) {
        const rangeStart = Math.floor(item.itemPower / this.groupRange) * this.groupRange;
        const key = rangeStart;

        if (!grouped[key]) {
          grouped[key] = [];
        }

        grouped[key].push(item);
      }
    });
    this.groupedItems = grouped;
  }

  updateGroupedItemsArray(): void {
    this.groupedItemsArray = Object.entries(this.groupedItems).map(([key, value]) => ({ key: Number(key), value }));
    // Optionally, sort the array based on keys
    this.groupedItemsArray.sort((a, b) => a.key - b.key);
  }

  getCheapestItem(items: ItemWithQualityAndPower[]): ItemWithQualityAndPower {
    return items.reduce((minObj, currentObj) => ((currentObj.cheapestPrice?.sellPriceMin ?? Number.POSITIVE_INFINITY) < (minObj.cheapestPrice?.sellPriceMin ?? Number.POSITIVE_INFINITY) ? currentObj : minObj), items[0]);
  }

  formatFooterText(item: ItemWithQualityAndPower): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(`
      <div>
        <strong>Quality:</strong> ${item.quality.name} |
        <strong>Total Power:</strong> <span>${item.itemPower.toFixed(0)}</span> <strong> => </strong>
        <strong>Base Power:</strong> <span>${item.basePower.toFixed(0)}</span>, 
        <strong>Quality Power:</strong> <span>${item.qualityBonus.toFixed(0)}</span>, 
        <strong>Mastery Bonus Power:</strong> <span>${item.masteryBonus.toFixed(0)}</span>, 
        <strong>Mastery Modifier Power:</strong> <span>${item.masteryModifier.toFixed(0)}</span>
      </div>
    `);
  }
  refreshPrices(): void {
    this.updatePrices(this.items ?? []);
    this.updateHistories(this.items ?? []);
  }
}
